/* eslint-disable react/no-unescaped-entities -- This file contains prose with apostrophes */
import React from "react";
import { Button, BUTTON_SIZE, BUTTON_THEME } from "App/core/Button";
import { ReactComponent as CaratLeftSVG } from "App/common/icons-v2/carat-left.svg";
import "./About.scss";

const Link = ({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="generate-comments__about-link"
  >
    {children}
  </a>
);

interface AboutProps {
  onBack: () => void;
}

export const About = ({ onBack }: AboutProps) => {
  return (
    <div className="generate-comments__about">
      <Button
        theme={BUTTON_THEME.CTA}
        size={BUTTON_SIZE.SMALL}
        onClick={onBack}
        className="generate-comments__about-back"
        startIcon={<CaratLeftSVG />}
      >
        Back
      </Button>

      <div className="generate-comments__about-content">
        <section className="generate-comments__about-section">
          <h2 className="generate-comments__about-heading">About</h2>
          <p className="generate-comments__about-text">
            Highnote's AI comment generator is a playful way to explore how
            commenting on Highnote can engage listeners. Artists, curators, and
            audio storytellers can add timestamped, karaoke-style annotations to
            their tracks to reveal deeper meaning, explain creative choices,
            spark debate, or share entertaining commentary. Artists can share on
            Highnote to build pre-release excitement, share exclusive commentary
            with their most devoted listeners, or create privately alongside
            other artists.
          </p>
          <p className="generate-comments__about-text">
            Highnote is led by co-founders Jordan Bradley and Paulina Vo. Our
            aim is to empower artists, their teams, and their communities with
            meaningful sharing and discussion tools that both smooth the
            creative process and invite listeners into it. We're seeking
            investors and partners who want to help amplify this mission. If
            that's you, reach out at
            <strong>jordan@highnote.fm</strong>.
          </p>
        </section>

        <section className="generate-comments__about-section">
          <h2 className="generate-comments__about-heading">How it Works</h2>
          <p className="generate-comments__about-text">
            The AI comment generator does not train AI models on uploaded audio
            or lyrics. While the AI personas are inspired by real people, they
            are entirely fictional and exist solely to demonstrate Highnote's
            underlying discussion experience. We encourage artists, audio
            storytellers, and curators to create their own Spaces on Highnote
            using audio they have permission to share, and to leave thoughtful
            commentary that engages their community. Here's how the AI comment
            generator works:
          </p>

          <ol className="generate-comments__about-list">
            <li className="generate-comments__about-list-item">
              <strong>You upload audio to Highnote</strong>
              <br />
              By creating an account and uploading to Highnote, you agree to our
              standard{" "}
              <Link href="https://www.highnote.fm/legal/privacy-policy">
                privacy policy
              </Link>{" "}
              and{" "}
              <Link href="https://www.highnote.fm/legal/terms-of-service">
                terms of service
              </Link>
              . Highnote does not claim ownership or any license to your
              uploads. Highnote does not train AI models on user-uploaded audio.
            </li>
            <li className="generate-comments__about-list-item">
              <strong>Lyric Transcription via MusicAI</strong>
              <br />
              When using the AI generator, your audio is sent to the{" "}
              <Link href="https://music.ai/">MusicAI API</Link> to isolate the
              main vocals from the rest of the song (aka Stem Separation) and to
              decode timestamped lyric transcription. MusicAI does not train on
              user-uploaded audio.
            </li>
            <li className="generate-comments__about-list-item">
              <strong>Persona-driven comments via ChatGPT</strong>
              <br />
              Your timestamped lyric transcript is sent to OpenAI (ChatGPT),
              which generates imaginative, persona-styled commentary based on
              the character you choose. OpenAI does not train on any lyrics
              Highnote sends on your behalf. Training only occurs when a
              business explicitly opts in—and Highnote does not.{" "}
              <Link href="https://openai.com/enterprise-privacy/">
                Read more here.
              </Link>
            </li>
            <li className="generate-comments__about-list-item">
              <strong>Comments saved to your track.</strong>
              <br />
              The generated comments are saved on your track inside Highnote and
              attributed either to the fictional persona's account or your
              own—depending on the option you select.
            </li>
          </ol>
        </section>
      </div>
    </div>
  );
};
